import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { 
  ArrowLeft, Shield, Users, CheckCircle, XCircle, Clock, 
  GraduationCap, Loader2, Search, UserPlus, UserCheck,
  LogOut, Home, Trash2, Mail
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import EdpiesLogo from '@/components/EdpiesLogo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminUserApproval = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState({ pending: 0, approved: 0, byType: {} });
  const [selectedUser, setSelectedUser] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [processingId, setProcessingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('pending');
  const [isAdmin, setIsAdmin] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [sendingSummaryId, setSendingSummaryId] = useState(null);

  useEffect(() => {
    checkAdminStatus();
  }, []);

  useEffect(() => {
    if (isAdmin) {
      loadUsers();
    }
  }, [isAdmin, filterStatus, filterType]);

  const checkAdminStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/auth');
        return;
      }

      const response = await axios.get(`${API}/admin/check`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!response.data.isAdmin) {
        toast.error('Admin access required');
        navigate('/');
        return;
      }

      setIsAdmin(true);
    } catch (error) {
      toast.error('Access denied');
      navigate('/');
    }
  };

  const loadUsers = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (filterStatus !== 'all') params.append('status', filterStatus);
      if (filterType !== 'all') params.append('user_type', filterType);
      
      const response = await axios.get(`${API}/admin/all-users?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setUsers(response.data.users || []);
      setStats(response.data.stats || { pending: 0, approved: 0, byType: {} });
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (userId) => {
    setProcessingId(userId);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/admin/approve-user/${userId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('User approved successfully!');
      loadUsers();
      setSelectedUser(null);
    } catch (error) {
      toast.error('Failed to approve user');
    } finally {
      setProcessingId(null);
    }
  };

  const handleReject = async (userId, deleteUser = false) => {
    if (!deleteUser && !rejectionReason.trim()) {
      toast.error('Please provide a reason for rejection');
      return;
    }
    
    setProcessingId(userId);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/admin/reject-user/${userId}`, {
        reason: rejectionReason,
        delete: deleteUser
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(deleteUser ? 'User deleted' : 'User rejected');
      loadUsers();
      setSelectedUser(null);
      setRejectionReason('');
    } catch (error) {
      toast.error('Failed to process request');
    } finally {
      setProcessingId(null);
    }
  };

  const handleBulkApprove = async () => {
    if (selectedUsers.length === 0) {
      toast.error('No users selected');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/admin/bulk-approve-users`, {
        user_ids: selectedUsers
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`${response.data.count} users approved`);
      setSelectedUsers([]);
      loadUsers();
    } catch (error) {
      toast.error('Failed to bulk approve');
    }
  };

  const toggleUserSelection = (userId) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleSendWeeklySummary = async (user) => {
    if (!window.confirm(`Send last week's summary email for ${user.name} to their linked parent?`)) {
      return;
    }
    setSendingSummaryId(user.id);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API}/weekly-summary/send/${user.id}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.sent) {
        toast.success(`Summary sent to ${res.data.to}`);
      } else {
        toast.error(res.data.reason || 'Could not send summary');
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to send summary');
    } finally {
      setSendingSummaryId(null);
    }
  };

  const handleLogout = () => {
    localStorage.clear();
    window.location.replace('/');
  };

  const getUserTypeBadge = (userType) => {
    const types = {
      student: { color: 'bg-blue-500', icon: GraduationCap },
      parent: { color: 'bg-purple-500', icon: Users },
      tutor: { color: 'bg-green-500', icon: UserCheck }
    };
    const config = types[userType] || types.student;
    const Icon = config.icon;
    return (
      <Badge className={`${config.color} text-white`}>
        <Icon className="h-3 w-3 mr-1" />
        {userType?.charAt(0).toUpperCase() + userType?.slice(1)}
      </Badge>
    );
  };

  const getStatusBadge = (user) => {
    if (user.isRejected) {
      return <Badge className="bg-red-500"><XCircle className="h-3 w-3 mr-1" /> Rejected</Badge>;
    }
    if (user.isApproved) {
      return <Badge className="bg-green-500"><CheckCircle className="h-3 w-3 mr-1" /> Approved</Badge>;
    }
    return <Badge className="bg-yellow-500"><Clock className="h-3 w-3 mr-1" /> Pending</Badge>;
  };

  const getInitials = (name) => {
    return name?.split(' ').map(n => n[0]).join('').toUpperCase() || '?';
  };

  const filteredUsers = users.filter(user => 
    user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (!isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 to-slate-800">
        <Loader2 className="h-8 w-8 animate-spin text-white" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <header className="bg-slate-800/50 backdrop-blur-sm border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate('/admin/tutors')} className="text-slate-300 hover:text-white hover:bg-white/10">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Admin Home
              </Button>
              <div className="flex items-center gap-2">
                <div className="p-2 rounded-lg bg-gradient-to-br from-violet-500 to-purple-600">
                  <UserPlus className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-white">User Registrations</h1>
                  <p className="text-xs text-slate-400">Approve or reject new users</p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={handleLogout} className="text-slate-300 hover:text-white hover:bg-white/10">
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </Button>
              <EdpiesLogo size="sm" />
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto p-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-3 rounded-lg bg-yellow-500/20">
                <Clock className="h-6 w-6 text-yellow-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{stats.pending}</p>
                <p className="text-xs text-slate-400">Pending</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-3 rounded-lg bg-green-500/20">
                <CheckCircle className="h-6 w-6 text-green-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{stats.approved}</p>
                <p className="text-xs text-slate-400">Approved</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-3 rounded-lg bg-blue-500/20">
                <GraduationCap className="h-6 w-6 text-blue-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{stats.byType?.students || 0}</p>
                <p className="text-xs text-slate-400">Students</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-3 rounded-lg bg-purple-500/20">
                <Users className="h-6 w-6 text-purple-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{stats.byType?.parents || 0}</p>
                <p className="text-xs text-slate-400">Parents</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3 mb-6">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Search by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-slate-800/50 border-slate-700 text-white"
            />
          </div>
          <div className="flex gap-2">
            <Button
              variant={filterStatus === 'pending' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilterStatus('pending')}
              className={filterStatus === 'pending' ? 'bg-yellow-600' : 'border-slate-600 text-slate-300'}
            >
              Pending
            </Button>
            <Button
              variant={filterStatus === 'approved' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilterStatus('approved')}
              className={filterStatus === 'approved' ? 'bg-green-600' : 'border-slate-600 text-slate-300'}
            >
              Approved
            </Button>
            <Button
              variant={filterStatus === 'all' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilterStatus('all')}
              className={filterStatus === 'all' ? 'bg-slate-600' : 'border-slate-600 text-slate-300'}
            >
              All
            </Button>
          </div>
          <div className="flex gap-2">
            {['all', 'student', 'parent', 'tutor'].map(type => (
              <Button
                key={type}
                variant={filterType === type ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilterType(type)}
                className={filterType === type ? 'bg-purple-600' : 'border-slate-600 text-slate-300'}
              >
                {type === 'all' ? 'All Types' : type.charAt(0).toUpperCase() + type.slice(1)}
              </Button>
            ))}
          </div>
        </div>

        {/* Bulk Actions */}
        {selectedUsers.length > 0 && filterStatus === 'pending' && (
          <div className="flex items-center gap-3 mb-4 p-3 bg-purple-500/20 rounded-lg border border-purple-500/30">
            <span className="text-purple-300">{selectedUsers.length} users selected</span>
            <Button size="sm" onClick={handleBulkApprove} className="bg-green-600 hover:bg-green-500">
              <CheckCircle className="h-4 w-4 mr-1" /> Approve All
            </Button>
            <Button size="sm" variant="outline" onClick={() => setSelectedUsers([])} className="border-slate-600 text-slate-300">
              Clear Selection
            </Button>
          </div>
        )}

        {/* User List */}
        {loading ? (
          <div className="flex justify-center items-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-purple-400" />
          </div>
        ) : filteredUsers.length === 0 ? (
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-12 text-center">
              <UserCheck className="h-16 w-16 text-slate-600 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-slate-300 mb-2">
                {filterStatus === 'pending' ? 'No Pending Users' : 'No Users Found'}
              </h3>
              <p className="text-slate-500">
                {filterStatus === 'pending' ? 'All registrations have been processed!' : 'Try adjusting your filters'}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {/* Table Header */}
            <div className="hidden md:grid md:grid-cols-12 gap-4 px-4 py-2 text-xs font-semibold text-slate-400 uppercase tracking-wider">
              <div className="col-span-1"></div>
              <div className="col-span-3">Full Name</div>
              <div className="col-span-3">Email</div>
              <div className="col-span-2">Type / Status</div>
              <div className="col-span-1">Registered</div>
              <div className="col-span-2 text-right">Actions</div>
            </div>
            
            {filteredUsers.map((user) => (
              <Card 
                key={user.id} 
                className={`bg-slate-800/50 border-slate-700 hover:border-slate-600 transition-all ${
                  selectedUsers.includes(user.id) ? 'ring-2 ring-purple-500' : ''
                }`}
              >
                <CardContent className="p-4">
                  {/* Desktop View - Table Layout */}
                  <div className="hidden md:grid md:grid-cols-12 gap-4 items-center">
                    {/* Selection checkbox */}
                    <div className="col-span-1 flex items-center gap-2">
                      {!user.isApproved && !user.isRejected && (
                        <input
                          type="checkbox"
                          checked={selectedUsers.includes(user.id)}
                          onChange={() => toggleUserSelection(user.id)}
                          className="h-5 w-5 rounded border-slate-600 bg-slate-700 text-purple-500"
                        />
                      )}
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center text-sm font-bold text-white flex-shrink-0">
                        {getInitials(user.name)}
                      </div>
                    </div>
                    
                    {/* Full Name */}
                    <div className="col-span-3">
                      <p className="font-semibold text-white">{user.name || 'N/A'}</p>
                      {user.studentProfile?.yearGroup && (
                        <p className="text-xs text-slate-500">{user.studentProfile.yearGroup}</p>
                      )}
                    </div>
                    
                    {/* Email */}
                    <div className="col-span-3">
                      <p className="text-slate-300 text-sm break-all">{user.email}</p>
                    </div>
                    
                    {/* Type / Status */}
                    <div className="col-span-2 flex flex-wrap gap-1">
                      {getUserTypeBadge(user.userType)}
                      {getStatusBadge(user)}
                    </div>
                    
                    {/* Registered Date */}
                    <div className="col-span-1">
                      <p className="text-xs text-slate-500">
                        {new Date(user.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}
                      </p>
                    </div>
                    
                    {/* Actions */}
                    <div className="col-span-2 flex items-center justify-end gap-2">
                      {!user.isApproved && !user.isRejected && (
                        <>
                          <Button
                            size="sm"
                            onClick={() => handleApprove(user.id)}
                            disabled={processingId === user.id}
                            className="bg-green-600 hover:bg-green-500"
                          >
                            {processingId === user.id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <><CheckCircle className="h-4 w-4 mr-1" /> Approve</>
                            )}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setSelectedUser(user)}
                            className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                          >
                            <XCircle className="h-4 w-4" />
                          </Button>
                        </>
                      )}
                      {(user.isApproved || user.isRejected) && (
                        <>
                          {user.userType === 'student' && user.isApproved && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleSendWeeklySummary(user)}
                              disabled={sendingSummaryId === user.id}
                              className="border-purple-500/50 text-purple-300 hover:bg-purple-500/10"
                              title="Send weekly summary to parent"
                              data-testid={`send-summary-btn-${user.id}`}
                            >
                              {sendingSummaryId === user.id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <Mail className="h-4 w-4" />
                              )}
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleReject(user.id, true)}
                            disabled={processingId === user.id}
                            className="border-slate-600 text-slate-400 hover:text-red-400 hover:border-red-500/50"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                  
                  {/* Mobile View - Card Layout */}
                  <div className="md:hidden">
                    <div className="flex items-start gap-3">
                      {!user.isApproved && !user.isRejected && (
                        <input
                          type="checkbox"
                          checked={selectedUsers.includes(user.id)}
                          onChange={() => toggleUserSelection(user.id)}
                          className="h-5 w-5 mt-1 rounded border-slate-600 bg-slate-700 text-purple-500"
                        />
                      )}
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center text-sm font-bold text-white flex-shrink-0">
                        {getInitials(user.name)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-white">{user.name || 'N/A'}</p>
                        <p className="text-sm text-purple-300 break-all">{user.email}</p>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {getUserTypeBadge(user.userType)}
                          {getStatusBadge(user)}
                        </div>
                        <p className="text-xs text-slate-500 mt-1">
                          Registered: {new Date(user.createdAt).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 mt-3 pt-3 border-t border-slate-700">
                      {!user.isApproved && !user.isRejected && (
                        <>
                          <Button
                            size="sm"
                            onClick={() => handleApprove(user.id)}
                            disabled={processingId === user.id}
                            className="flex-1 bg-green-600 hover:bg-green-500"
                          >
                            {processingId === user.id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <><CheckCircle className="h-4 w-4 mr-1" /> Approve</>
                            )}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setSelectedUser(user)}
                            className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                          >
                            <XCircle className="h-4 w-4 mr-1" /> Reject
                          </Button>
                        </>
                      )}
                      {(user.isApproved || user.isRejected) && (
                        <>
                          {user.userType === 'student' && user.isApproved && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleSendWeeklySummary(user)}
                              disabled={sendingSummaryId === user.id}
                              className="flex-1 border-purple-500/50 text-purple-300 hover:bg-purple-500/10"
                              data-testid={`send-summary-btn-mobile-${user.id}`}
                            >
                              {sendingSummaryId === user.id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <><Mail className="h-4 w-4 mr-1" /> Send Summary</>
                              )}
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleReject(user.id, true)}
                            disabled={processingId === user.id}
                            className="border-slate-600 text-slate-400 hover:text-red-400 hover:border-red-500/50"
                          >
                            <Trash2 className="h-4 w-4 mr-1" /> Delete
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Rejection Modal */}
        {selectedUser && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <Card className="bg-slate-800 border-slate-700 max-w-md w-full">
              <CardHeader>
                <CardTitle className="text-white">Reject Registration</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-red-500 to-rose-600 flex items-center justify-center text-lg font-bold text-white">
                    {getInitials(selectedUser.name)}
                  </div>
                  <div>
                    <p className="font-semibold text-white">{selectedUser.name}</p>
                    <p className="text-sm text-slate-400">{selectedUser.email}</p>
                  </div>
                </div>
                
                <div>
                  <label className="text-sm text-slate-400 block mb-2">Reason for rejection</label>
                  <Textarea
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    placeholder="Provide a reason..."
                    className="bg-slate-700 border-slate-600 text-white"
                  />
                </div>
                
                <div className="flex gap-3">
                  <Button
                    onClick={() => handleReject(selectedUser.id, false)}
                    disabled={processingId === selectedUser.id}
                    className="flex-1 bg-red-600 hover:bg-red-500"
                  >
                    {processingId === selectedUser.id ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      'Reject & Keep'
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => handleReject(selectedUser.id, true)}
                    disabled={processingId === selectedUser.id}
                    className="flex-1 border-slate-600 text-slate-300"
                  >
                    Delete Account
                  </Button>
                </div>
                <Button
                  variant="ghost"
                  onClick={() => { setSelectedUser(null); setRejectionReason(''); }}
                  className="w-full text-slate-400"
                >
                  Cancel
                </Button>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminUserApproval;
